/**
 * Barème de TVA daté et paramétrable (FR-TVA-01).
 *
 * Chaque taux a une date d'effet : un changement de loi s'ajoute comme NOUVELLE ligne datée, sans
 * toucher aux factures déjà émises (leur taux est figé sur la ligne de facture).
 *
 * CLOS PAR LA DGI (18/10/2026) : 10 % et 5 % ne sont pas en conflit, ce sont deux taux réduits qui coexistent,
 * chacun pour sa liste de produits (CGI art. 221 ; https://dgi.ga/imposition-des-personnes-morales/taxes-sur-le-chiffre-daffaires/tva/).
 * Aucun taux n'est modifié : 18 % / 10 % / 5 % / 0 %. Reste à rattacher chaque produit à son taux (catalogue produit),
 * ce que ce barème ne fait pas. Un changement de loi s'ajoute comme une ligne datée (stockage `compta.bareme_tva`).
 */

export type CategorieTva = 'normal' | 'reduit' | 'intermediaire' | 'exonere';
export type StatutTauxTva = 'valide' | 'a_confirmer';

export interface TauxTvaDate {
  readonly categorie: CategorieTva;
  /** Taux en fraction (0,18 = 18 %). */
  readonly taux: number;
  /** Date d'effet, AAAA-MM-JJ (incluse). `1970-01-01` = « depuis toujours » (date non documentée). */
  readonly depuis: string;
  readonly statut: StatutTauxTva;
  readonly source: string;
}

export const BAREME_TVA_PAR_DEFAUT: readonly TauxTvaDate[] = [
  {
    categorie: 'normal',
    taux: 0.18,
    depuis: '1970-01-01',
    statut: 'valide',
    source: 'CGI Gabon, loi 019/2022 : taux normal 18 % (code, PRD §7.4 et référentiel v2 concordent).',
  },
  {
    categorie: 'intermediaire',
    taux: 0.1,
    depuis: '1970-01-01',
    statut: 'valide',
    source: 'CGI art. 221 : taux réduit de 10 % (matériel de pêche, pièces détachées automobiles, sucre, viandes et volailles importées, etc.). Catégorie « intermédiaire » dans le code.',
  },
  {
    categorie: 'reduit',
    taux: 0.05,
    depuis: '1970-01-01',
    statut: 'valide',
    source: 'CGI art. 221 : taux réduit de 5 % (ciment, fer à béton, eau minérale locale, lessive, ordinateurs, conserves, eau et électricité sur compteurs sociaux). Coexiste avec le 10 %.',
  },
  {
    categorie: 'exonere',
    taux: 0,
    depuis: '1970-01-01',
    statut: 'valide',
    source: 'Opérations exonérées.',
  },
];

export class BaremeTvaInvalideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BaremeTvaInvalideError';
  }
}

const FORMAT_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CATEGORIES: readonly CategorieTva[] = ['normal', 'reduit', 'intermediaire', 'exonere'];

/** Valide un barème (catégories connues, taux entre 0 et 1, dates ISO, pas de doublon catégorie+date). */
export function validerBaremeTva(bareme: readonly TauxTvaDate[]): void {
  const vus = new Set<string>();
  for (const l of bareme) {
    if (!CATEGORIES.includes(l.categorie)) throw new BaremeTvaInvalideError(`Catégorie de TVA inconnue : ${String(l.categorie)}`);
    if (!Number.isFinite(l.taux) || l.taux < 0 || l.taux > 1) {
      throw new BaremeTvaInvalideError(`Taux de TVA invalide (${l.categorie}) : ${l.taux} (entre 0 et 1 attendu).`);
    }
    if (!FORMAT_DATE.test(l.depuis) || Number.isNaN(Date.parse(l.depuis))) {
      throw new BaremeTvaInvalideError(`Date d'effet invalide (${l.categorie}) : ${l.depuis} (AAAA-MM-JJ attendu).`);
    }
    const cle = `${l.categorie}@${l.depuis}`;
    if (vus.has(cle)) throw new BaremeTvaInvalideError(`Deux taux ${l.categorie} pour la même date ${l.depuis}.`);
    vus.add(cle);
  }
  for (const c of CATEGORIES) {
    if (!bareme.some((l) => l.categorie === c)) throw new BaremeTvaInvalideError(`Aucun taux pour la catégorie ${c}.`);
  }
}

/** Barème de l'entreprise : défauts + lignes datées ajoutées (une ligne de même catégorie et date remplace le défaut). */
export function resoudreBaremeTva(ajouts?: readonly TauxTvaDate[] | null): readonly TauxTvaDate[] {
  const out = new Map<string, TauxTvaDate>();
  for (const l of [...BAREME_TVA_PAR_DEFAUT, ...(ajouts ?? [])]) out.set(`${l.categorie}@${l.depuis}`, l);
  const bareme = [...out.values()].sort((a, b) =>
    a.categorie === b.categorie ? a.depuis.localeCompare(b.depuis) : a.categorie.localeCompare(b.categorie),
  );
  validerBaremeTva(bareme);
  return bareme;
}

/** Taux applicable à une catégorie à une date (AAAA-MM-JJ) : la ligne la plus récente déjà en vigueur. */
export function tauxTvaA(
  categorie: CategorieTva,
  date: string,
  bareme: readonly TauxTvaDate[] = BAREME_TVA_PAR_DEFAUT,
): number {
  const jour = date.slice(0, 10);
  let retenu: TauxTvaDate | undefined;
  for (const l of bareme) {
    if (l.categorie !== categorie || l.depuis > jour) continue;
    if (!retenu || l.depuis > retenu.depuis) retenu = l;
  }
  if (!retenu) throw new BaremeTvaInvalideError(`Aucun taux ${categorie} en vigueur au ${jour}.`);
  return retenu.taux;
}

const LIBELLES: Record<CategorieTva, (pct: string) => string> = {
  normal: (pct) => `TVA ${pct} %`,
  intermediaire: (pct) => `TVA ${pct} %`,
  reduit: (pct) => `TVA ${pct} %`,
  exonere: (pct) => `Exonéré ${pct} %`,
};

/** Options de liste déroulante (valeur = taux), libellés dérivés des taux pour éviter toute divergence. */
export function optionsTvaPourDate(
  date: string,
  bareme: readonly TauxTvaDate[] = BAREME_TVA_PAR_DEFAUT,
): { value: string; label: string }[] {
  const ordre: CategorieTva[] = ['normal', 'intermediaire', 'reduit', 'exonere'];
  const vus = new Set<string>();
  const options: { value: string; label: string }[] = [];
  for (const c of ordre) {
    const taux = tauxTvaA(c, date, bareme);
    const value = String(taux);
    if (vus.has(value)) continue;
    vus.add(value);
    const pct = String(Math.round(taux * 10000) / 100).replace('.', ',');
    options.push({ value, label: LIBELLES[c](pct) });
  }
  return options;
}
